import { createContext } from "react";

import { getCookie } from "utils/apollo";

/*
 * Client helpers for the CLI OAuth login flow (`signals` and other CLIs).
 * The CLI opens the backend authorization endpoint, which validates the request,
 * stores it, and redirects here with an opaque id. The browser never carries the
 * authorization parameters, only that id.
 */

const CLI_CONSENT_PATH = "/auth/cli";
const CLI_ERROR_PATH = "/auth/cli/error";
const CLI_OAUTH_STORAGE_KEY = "cli_oauth_request_id";

interface ICliOauthRequest {
  clientId: string;
  clientName: string;
}

/*
 * True only while the CLI login page owns the login UI. The shared provider
 * buttons read this to choose their redirect target, so a leftover stash can
 * never make the normal web login return to the CLI callback.
 */
const cliOauthContext = createContext(false);

const readCliOauthRequestIdFromUrl = (): string | null =>
  new URLSearchParams(window.location.search).get("request_id");

const stashCliOauthRequestId = (requestId: string): void => {
  sessionStorage.setItem(CLI_OAUTH_STORAGE_KEY, requestId);
};

const readCliOauthRequestId = (): string | null =>
  sessionStorage.getItem(CLI_OAUTH_STORAGE_KEY);

const clearCliOauthRequestId = (): void => {
  sessionStorage.removeItem(CLI_OAUTH_STORAGE_KEY);
};

const cliOauthCallbackUri = (): string =>
  `${window.location.origin}/auth/oauth/callback`;

const cliConsentUri = (requestId: string): string =>
  `${CLI_CONSENT_PATH}?${new URLSearchParams([["request_id", requestId]]).toString()}`;

/*
 * Read the pending request the backend stored. A 401 means the browser has no
 * platform session yet, which is the caller's cue to render the login providers.
 */
const fetchCliOauthRequest = async (
  requestId: string,
): Promise<ICliOauthRequest | "unauthenticated" | null> => {
  const unauthorized = 401;
  const response = await fetch(
    `${window.location.origin}/auth/oauth/authorize/${requestId}`,
    { credentials: "same-origin" },
  );
  if (response.status === unauthorized) {
    return "unauthenticated";
  }
  if (!response.ok) {
    return null;
  }
  const data = (await response.json()) as {
    client_id: string;
    client_name?: string;
  };

  /*
   * The name comes from the platform's client registry, never from anything the
   * client sent. Falling back to the id keeps the screen usable against a platform
   * that predates the registry rather than naming the requester "undefined".
   */
  return {
    clientId: data.client_id,
    clientName: data.client_name ?? data.client_id,
  };
};

/*
 * Answer the request and hand the browser to the CLI's loopback server, whether
 * the answer was yes or no. The backend returns the location instead of a 302
 * because a fetch would follow the redirect itself and fail CORS against the
 * loopback origin. Both answers travel the same way: RFC 6749 4.1.2.1 has a
 * refusal redirect back too, and a CLI that is never told waits out its timeout.
 */
const answerCliOauthRequest = async (
  requestId: string,
  decision: "approve" | "deny",
): Promise<void> => {
  clearCliOauthRequestId();
  try {
    const response = await fetch(
      `${window.location.origin}/auth/oauth/authorize/${requestId}/${decision}`,
      {
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCookie("csrftoken"),
        },
        method: "POST",
      },
    );
    if (!response.ok) {
      window.location.assign(CLI_ERROR_PATH);

      return;
    }
    const data = (await response.json()) as { redirect: string };
    window.location.assign(data.redirect);
  } catch {
    window.location.assign(CLI_ERROR_PATH);
  }
};

const approveCliOauthRequest = async (requestId: string): Promise<void> =>
  answerCliOauthRequest(requestId, "approve");

const denyCliOauthRequest = async (requestId: string): Promise<void> =>
  answerCliOauthRequest(requestId, "deny");

export type { ICliOauthRequest };
export {
  CLI_CONSENT_PATH,
  CLI_ERROR_PATH,
  approveCliOauthRequest,
  clearCliOauthRequestId,
  cliConsentUri,
  cliOauthCallbackUri,
  cliOauthContext,
  denyCliOauthRequest,
  fetchCliOauthRequest,
  readCliOauthRequestId,
  readCliOauthRequestIdFromUrl,
  stashCliOauthRequestId,
};
